import { useState, useEffect, useCallback } from 'react';

type SetValue<T> = (value: T | ((prev: T) => T)) => Promise<void>;
type RemoveValue = () => Promise<void>;

export function useAsyncStorage<T>(key: string, defaultValue: T | null = null): [T | null, SetValue<T>, RemoveValue] {
    const [storedValue, setStoredValue] = useState<T | null>(() => {
        try {
            const item = localStorage.getItem(key);
            return item ? (JSON.parse(item) as T) : defaultValue;
        } catch (error) {
            console.error('Error reading from localStorage', error);
            return defaultValue;
        }
    });

    useEffect(() => {
        if (storedValue !== null && storedValue !== undefined) {
            try {
                localStorage.setItem(key, JSON.stringify(storedValue));
            } catch (error) {
                console.error('Error setting localStorage item', error);
            }
        }
    }, [key, storedValue]);

    const setValue: SetValue<T> = useCallback(
        async value => {
            try {
                const valueToStore = value instanceof Function ? value(storedValue as T) : value;
                setStoredValue(valueToStore);
                localStorage.setItem(key, JSON.stringify(valueToStore));
            } catch (error) {
                console.error('Error setting localStorage item', error);
            }
        },
        [key, storedValue],
    );

    const removeValue: RemoveValue = useCallback(async () => {
        try {
            localStorage.removeItem(key);
            setStoredValue(null);
        } catch (error) {
            console.error('Error removing localStorage item', error);
        }
    }, [key]);

    return [storedValue, setValue, removeValue];
}
